import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { Api } from '../../services/Api/Api';

const CreateUserModal = ({ isOpen, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        password: '',
        company: ''
    });
    const [loading, setLoading] = useState(false);

    // Réinitialiser le formulaire
    const resetForm = () => {
        setFormData({
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            address: '',
            password: '',
            company: ''
        });
    };

    // Gérer les changements dans le formulaire
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Créer un nouvel utilisateur
    const createUser = async () => {
        try {
            setLoading(true);

            console.log('Début de la création utilisateur...');

            const payload = {
                email: formData.email,
                password: formData.password,
                first_name: formData.first_name,
                last_name: formData.last_name,
                phone: formData.phone,
                address: formData.address
            };

            // Ajouter company seulement si elle est fournie
            if (formData.company.trim()) {
                payload.company = formData.company;
            }

            console.log('Payload envoyé:', payload);

            const response = await Api.post('formalite/users-formalite/create-admin-formalite/', payload);

            resetForm();
            onSuccess('Utilisateur créé avec succès !');

        } catch (error) {
            console.error('Erreur lors de la création:', error);
            console.error('Détails de l\'erreur:', {
                message: error.message,
                status: error.status,
                response: error.response
            });
            alert('Erreur lors de la création de l\'utilisateur: ' + (error.message || 'Erreur inconnue'));
        } finally {
            setLoading(false);
        }
    };

    // Gérer la soumission du formulaire
    const handleSubmit = async (e) => {
        // CRITIQUE: Empêcher le comportement par défaut
        e.preventDefault();
        e.stopPropagation();

        console.log('Soumission du formulaire de création interceptée');

        // Validation simple
        if (!formData.first_name || !formData.last_name || !formData.email) {
            alert('Veuillez remplir tous les champs obligatoires (prénom, nom, email)');
            return;
        }

        if (!formData.password) {
            alert('Le mot de passe est obligatoire pour la création');
            return;
        }

        // Validation email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            alert('Veuillez entrer un email valide');
            return;
        }

        await createUser();
    };

    // Gérer la fermeture du modal
    const handleClose = () => {
        if (!loading) {
            resetForm();
            onClose();
        }
    };

    // Gérer la touche Échap
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen && !loading) {
                handleClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isOpen, loading]);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
            <div className="modal-content">
                <div className="modal-header">
                    <h3>Ajouter un utilisateur</h3>
                    <button
                        type="button"
                        className="modal-close"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Utilisation d'une div au lieu d'un form - EXACTEMENT comme UpdateUserModal */}
                <div className="modal-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="create_first_name">Prénom *</label>
                            <input
                                type="text"
                                id="create_first_name"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleInputChange}
                                disabled={loading}
                                placeholder="Prénom de l'utilisateur"
                                autoComplete="given-name"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="create_last_name">Nom *</label>
                            <input
                                type="text"
                                id="create_last_name"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleInputChange}
                                disabled={loading}
                                placeholder="Nom de l'utilisateur"
                                autoComplete="family-name"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="create_email">Email *</label>
                            <input
                                type="email"
                                id="create_email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                disabled={loading}
                                placeholder="email@exemple.com"
                                autoComplete="email"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="create_phone">Téléphone</label>
                            <input
                                type="tel"
                                id="create_phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                disabled={loading}
                                placeholder="+2250700000000"
                                autoComplete="tel"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="create_password">Mot de passe *</label>
                            <input
                                type="password"
                                id="create_password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                disabled={loading}
                                placeholder="Mot de passe sécurisé"
                                autoComplete="new-password"
                                minLength="6"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="create_company">Entreprise</label>
                            <input
                                type="text"
                                id="create_company"
                                name="company"
                                value={formData.company}
                                onChange={handleInputChange}
                                disabled={loading}
                                placeholder="Nom de l'entreprise"
                                autoComplete="organization"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="create_address">Adresse</label>
                        <textarea
                            id="create_address"
                            name="address"
                            value={formData.address}
                            onChange={handleInputChange}
                            rows="3"
                            disabled={loading}
                            placeholder="Adresse complète"
                            autoComplete="address-line1"
                        />
                    </div>

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            className="save-button"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            <Save size={16} />
                            {loading ? 'Création en cours...' : 'Créer l\'utilisateur'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateUserModal;